import React from 'react';

const StatBar = ({ value, max, color }) => (
  <div className="h-1.5 bg-[rgba(0,0,0,0.3)] rounded-full overflow-hidden flex-1">
    <div
      className="h-full rounded-full transition-all duration-700"
      style={{ width: `${Math.min(100, (value / (max || 1)) * 100)}%`, backgroundColor: color }}
    />
  </div>
);

const PitcherBlock = ({ pitcher, side }) => {
  if (!pitcher) return (
    <div className="flex-1 flex items-center justify-center text-[#658d71] text-xs uppercase tracking-widest py-4">
      No data
    </div>
  );

  const p = pitcher.stats?.pitching;
  const name = pitcher.person?.fullName || '--';
  const lastName = name.split(' ').slice(1).join(' ') || name;
  const era = p?.era ?? '--';
  const ip = p?.inningsPitched ?? '--';
  const k = p?.strikeOuts ?? 0;
  const bb = p?.baseOnBalls ?? 0;
  const er = p?.earnedRuns ?? 0;
  const hits = p?.hits ?? 0;
  const pitches = p?.pitchesThrown ?? '--';
  const strikes = p?.strikes ?? '--';
  const isRight = side === 'right';

  return (
    <div className={`flex-1 flex flex-col gap-2 ${isRight ? 'items-end text-right' : 'items-start text-left'}`}>
      <div>
        <p className="text-[9px] text-[#658d71] uppercase tracking-widest font-bold">
          {isRight ? 'Home SP' : 'Away SP'}
        </p>
        <p className="text-sm font-black uppercase tracking-wide text-[#d8ceb3] leading-tight">{lastName}</p>
      </div>
      <div className={`grid grid-cols-3 gap-1.5 w-full`}>
        {[['IP', ip, 'text-[#d8ceb3]'], ['K', k, 'text-[#a9c9b1]'], ['ER', er, er > 3 ? 'text-[#c4333e]' : 'text-white'],
          ['BB', bb, 'text-white'], ['H', hits, 'text-white'], ['P', pitches, 'text-[#658d71]']
        ].map(([label, val, cls]) => (
          <div key={label} className="flex flex-col items-center bg-[rgba(0,0,0,0.2)] border border-[#658d71] p-1">
            <span className="text-[8px] uppercase tracking-widest text-[#658d71] font-bold">{label}</span>
            <span className={`text-sm font-black tabular-nums ${cls}`}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PitchingMatchup = ({ boxScore, awayAbbr, homeAbbr }) => {
  if (!boxScore) return null;

  const getStarter = (side) => {
    const players = Object.values(boxScore.teams?.[side]?.players || {});
    return players
      .filter(p => p.stats?.pitching && parseFloat(p.stats.pitching.inningsPitched) >= 3)
      .sort((a, b) => parseFloat(b.stats.pitching.inningsPitched) - parseFloat(a.stats.pitching.inningsPitched))[0] || null;
  };

  const awayStarter = getStarter('away');
  const homeStarter = getStarter('home');

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
      <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Starting Pitchers</span>
      </div>
      <div className="p-3 flex items-start gap-3">
        <PitcherBlock pitcher={awayStarter} side="left" />
        <div className="flex flex-col items-center shrink-0 pt-6 gap-1">
          <span className="text-[#658d71] text-xs font-black">VS</span>
        </div>
        <PitcherBlock pitcher={homeStarter} side="right" />
      </div>
    </div>
  );
};

export default PitchingMatchup;